/**
 * @fileoverview Shared `DashboardThemeEditor` component for the left-pane "Theme & Branding"
 * accordion in both `DashboardBuilderPage` (`tenant-web`) and `SystemDashboardBuilderPage` (`system-web`).
 * @module ui-admin/components/DashboardThemeEditor/DashboardThemeEditor
 *
 * Responsibilities: - Render theme preset pills (`Brand`, `Slate`, `Clean Light`, `Command Center Dark`,
 *                     `High Contrast`) and custom color/font overrides (`pageBg`, `cardBg`, `accentColor`,
 *                     `fontFamily`).
 * Dependencies:     - React
 *                   - @firesquirrel/local-db (DashboardThemeConfig)
 *                   - ./dashboardThemePresets (DASHBOARD_THEME_PRESETS, resolveDashboardTheme)
 * Architecture:     - Presentation-only controlled component (`value` + `onChange`).
 * Notes:            - Closes gap G-27.
 */

import React from 'react';
import type { DashboardThemeConfig } from '@firesquirrel/local-db';
import { DASHBOARD_THEME_PRESETS, resolveDashboardTheme } from './dashboardThemePresets';

/**
 * @contract DashboardThemeEditorProps
 * Purpose: Props for `DashboardThemeEditor`.
 * Fields:  value, onChange, disabled?
 * Rules:   Controlled component; emits full `DashboardThemeConfig` on preset or custom field change.
 * Sync:    N/A.
 */
export interface DashboardThemeEditorProps {
  value?: DashboardThemeConfig;
  onChange: (next: DashboardThemeConfig) => void;
  disabled?: boolean;
}

const FONT_OPTIONS = [
  { value: 'Inter, system-ui, sans-serif', label: 'Inter (Default)' },
  { value: 'Roboto, system-ui, sans-serif', label: 'Roboto' },
  { value: 'Georgia, serif', label: 'Georgia (Serif)' },
  { value: 'ui-monospace, monospace', label: 'Monospace' },
];

/**
 * @component DashboardThemeEditor
 * Purpose:     Allow authors to choose a dashboard theme preset or customize page/card/accent colors and font.
 * Props:       DashboardThemeEditorProps.
 * Behavior:    Selecting a preset applies its colors; editing any color sets `presetId: 'custom'`.
 * Data Source: none (controlled).
 * Constraints: presentation-only; no persistence.
 * Performance: stateless.
 */
export function DashboardThemeEditor({
  value,
  onChange,
  disabled = false,
}: DashboardThemeEditorProps) {
  const resolved = resolveDashboardTheme(value);

  const handleSelectPreset = (presetId: NonNullable<DashboardThemeConfig['presetId']>) => {
    const preset = DASHBOARD_THEME_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    onChange({
      presetId: preset.id,
      ...preset.config,
    });
  };

  const handleCustomChange = (patch: Partial<DashboardThemeConfig>) => {
    onChange({
      ...resolved,
      ...patch,
      presetId: 'custom',
    });
  };

  return (
    <div className="space-y-3" data-testid="dashboard-theme-editor">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">Theme Preset</label>
        <div className="grid grid-cols-2 gap-1.5">
          {DASHBOARD_THEME_PRESETS.map((preset) => {
            const isSelected = resolved.presetId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                disabled={disabled}
                data-testid={`theme-preset-${preset.id}`}
                onClick={() => handleSelectPreset(preset.id)}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs font-medium text-left transition-colors ${
                  isSelected
                    ? 'border-orange-500 bg-orange-50 text-orange-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span
                  className="h-3 w-3 rounded-full shrink-0 border border-black/10"
                  style={{ backgroundColor: preset.config.accentColor }}
                />
                <span className="truncate">{preset.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-1">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Page BG</label>
          <input
            type="color"
            disabled={disabled}
            aria-label="Page background color"
            value={resolved.pageBg}
            onChange={(e) => handleCustomChange({ pageBg: e.target.value })}
            className="h-7 w-full rounded border border-gray-300 cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Card BG</label>
          <input
            type="color"
            disabled={disabled}
            aria-label="Card background color"
            value={resolved.cardBg}
            onChange={(e) => handleCustomChange({ cardBg: e.target.value })}
            className="h-7 w-full rounded border border-gray-300 cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Accent</label>
          <input
            type="color"
            disabled={disabled}
            aria-label="Accent color"
            value={resolved.accentColor}
            onChange={(e) => handleCustomChange({ accentColor: e.target.value })}
            className="h-7 w-full rounded border border-gray-300 cursor-pointer"
          />
        </div>
      </div>

      <div>
        <label className="block text-[11px] text-gray-500 mb-1">Font Family</label>
        <select
          disabled={disabled}
          aria-label="Dashboard font family"
          value={resolved.fontFamily}
          onChange={(e) => handleCustomChange({ fontFamily: e.target.value })}
          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md bg-white"
        >
          {FONT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default DashboardThemeEditor;
